import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import { SchedulerPanel } from "../components/scheduler";
import { TradingConfigPanel } from "../components/admin/TradingConfigPanel";
import { UISettingsPanel } from "../components/admin/UISettingsPanel";
import { TickerUniversePanel } from "../components/admin/TickerUniversePanel";
import { StripeTogglePanel } from "../components/admin/StripeTogglePanel";
import { PromoCodePanel } from "../components/admin/PromoCodePanel";
import { DiversifierWeightsPanel } from "../components/admin/DiversifierWeightsPanel";
import { AdminBannerPanel } from "../components/admin/AdminBannerPanel";
import { VideoCatalogPanel } from "../components/admin/VideoCatalogPanel";
import { FeatureFlagsPanel } from "../components/admin/FeatureFlagsPanel";
import OptimizerDefaultsPanel from "../components/admin/OptimizerDefaultsPanel";
import { FEATURES } from "../featureFlags";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Format timestamp from "2026-02-14 00:03:52 EST" to "2026-02-14 12:03:52 AM EST"
function formatTimestampTo12Hour(timestamp: string): string {
  // Extract the time portion (HH:MM:SS) and timezone
  const match = timestamp.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2}):(\d{2}) (.+)$/);
  if (!match) return timestamp;
  
  const [, date, hourStr, min, sec, tz] = match;
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12; // Convert 0 to 12, keep 1-11 as is, convert 12 to 12
  
  return `${date} ${hour}:${min}:${sec} ${ampm} ${tz}`;
}

interface WarmResult {
  ticker: string;
  status: string;
  records?: number;
  date_range?: string;
  error?: string;
}

interface WarmResponse {
  success: boolean;
  // API returns results as object {ticker: recordCount} not array
  results?: Record<string, number>;
  failed?: string[];
  tickersProcessed?: number;
  tickersFailed?: number;
  summary?: {
    success: number;
    failed: number;
  };
}

interface RecentDataRow {
  date: string;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
  adj_close?: number | null;
  volume?: number | null;
}

interface InspectResponse {
  ticker: string;
  exists: boolean;
  total_rows?: number;
  first_date?: string;
  last_date?: string;
  size_bytes?: number;
  error?: string;
  recent_data?: RecentDataRow[];
  // Mapped fields for display
  status?: string;
  records?: number;
  date_range?: string;
}

interface CachedTicker {
  ticker: string;
  records?: number;
  start_date?: string;
  end_date?: string;
  size_bytes?: number;
  error?: string;
}

interface CacheListResponse {
  tickers: CachedTicker[];
  total: number;
  source?: string;
  error?: string;
}

type SortField =
  | "ticker"
  | "records"
  | "start_date"
  | "end_date"
  | "size_bytes";
type SortDirection = "asc" | "desc";

// Admin section component - only renders for admins
function AdminSection() {
  const { user } = useAuthStore();
  const isAdmin = user?.roles?.some((role) => role.name === "TradeCraft_Admin");

  if (!isAdmin) return null;

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-purple-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-surface-100">
            Admin Tools
          </h2>
          <p className="text-sm text-surface-400">
            Administrative functions and monitoring
          </p>
        </div>
      </div>

      <div className="grid gap-3">
        <Link
          to="/admin/activity-logs"
          className="flex items-center justify-between p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:bg-surface-700/50 hover:border-surface-600 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-surface-100">Activity Logs</div>
              <div className="text-xs text-surface-400">View user activity and audit trail</div>
            </div>
          </div>
          <svg
            className="w-5 h-5 text-surface-500 group-hover:text-surface-300 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Link>

        <Link
          to="/admin/users"
          className="flex items-center justify-between p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:bg-surface-700/50 hover:border-surface-600 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-surface-100">User Management</div>
              <div className="text-xs text-surface-400">View user details, accounts, and investments</div>
            </div>
          </div>
          <svg
            className="w-5 h-5 text-surface-500 group-hover:text-surface-300 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Link>

        {/* Scheduler Management Panel */}
        <SchedulerPanel />

        {/* Trading Config Panel */}
        <TradingConfigPanel />

        {/* UI Settings Panel */}
        <UISettingsPanel />

        {/* Ticker Universe Panel */}
        <TickerUniversePanel />

        {/* Stripe Mode Toggle */}
        <StripeTogglePanel />

        {/* Promo Code Management */}
        <PromoCodePanel />

        {/* Feature Flags */}
        <FeatureFlagsPanel />

        {/* Diversifier Default Weights */}
        <DiversifierWeightsPanel />

        {/* Site Banner Management */}
        <AdminBannerPanel />

        {/* Video Catalog Management */}
        <VideoCatalogPanel />

        {/* Strategy Optimizer Defaults */}
        {FEATURES.OPTIMIZER && <OptimizerDefaultsPanel />}
      </div>
    </div>
  );
}

export function SettingsPage() {
  // Warm cache state
  const [tickers, setTickers] = useState("");
  const [startDate, setStartDate] = useState("2015-01-01");
  const [isWarming, setIsWarming] = useState(false);
  const [warmResults, setWarmResults] = useState<WarmResult[] | null>(null);
  const [warmError, setWarmError] = useState<string | null>(null);

  // Warm All state
  const [isWarmingAll, setIsWarmingAll] = useState(false);
  const [warmAllError, setWarmAllError] = useState<string | null>(null);
  const [warmAllResult, setWarmAllResult] = useState<{
    processed: number;
    failed: number;
    skipped?: number;
    gapsFilled?: number;
    nansFixed?: number;
    failedTickers?: { ticker: string; reason: string }[];
  } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [clearOldData, setClearOldData] = useState(false);

  // Warm Recent state
  const [isWarmingRecent, setIsWarmingRecent] = useState(false);
  const [warmRecentError, setWarmRecentError] = useState<string | null>(null);
  const [warmRecentResult, setWarmRecentResult] = useState<{
    processed: number;
    failed: number;
    skipped?: number;
    gapsFilled?: number;
    nansFixed?: number;
    failedTickers?: { ticker: string; reason: string }[];
  } | null>(null);
  const [showRecentPasswordModal, setShowRecentPasswordModal] = useState(false);
  const [recentPassword, setRecentPassword] = useState("");
  const [recentClearOldData, setRecentClearOldData] = useState(false);

  // Inspect cache state
  const [inspectTicker, setInspectTicker] = useState("");
  const [isInspecting, setIsInspecting] = useState(false);
  const [inspectResult, setInspectResult] = useState<InspectResponse | null>(
    null,
  );
  const [inspectError, setInspectError] = useState<string | null>(null);

  // Cached tickers list state
  const [cachedTickers, setCachedTickers] = useState<CachedTicker[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("ticker");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Cronjob status state
  const [cronjobStatus, setCronjobStatus] = useState<{
    has_run: boolean;
    timestamp_est?: string;
    tickers_processed?: number;
    duration_seconds?: number;
    status?: string;
    message?: string;
  } | null>(null);

  // Realtime refresh status state (for Today's Prices section)
  const [realtimeRefreshStatus, setRealtimeRefreshStatus] = useState<{
    has_run: boolean;
    timestamp_est?: string;
    tickers_processed?: number;
    duration_seconds?: number;
    status?: string;
    message?: string;
  } | null>(null);

  // Today's prices state
  const [todayPrices, setTodayPrices] = useState<{
    ticker: string;
    open: number | null;
    high: number | null;
    low: number | null;
    close: number;
    volume: number | null;
  }[]>([]);
  const [isLoadingTodayPrices, setIsLoadingTodayPrices] = useState(false);
  const [todayPricesError, setTodayPricesError] = useState<string | null>(null);
  const [todayPricesSearchQuery, setTodayPricesSearchQuery] = useState("");

  // Realtime refresh trigger state
  const [isRefreshingRealtime, setIsRefreshingRealtime] = useState(false);
  const [realtimeRefreshResult, setRealtimeRefreshResult] = useState<{
    success: boolean;
    message?: string;
    status?: string;
    tickers_updated?: number;
    tickers_total?: number;
    duration_seconds?: number;
    timestamp_est?: string;
    error?: string;
  } | null>(null);
  const [realtimeRefreshError, setRealtimeRefreshError] = useState<string | null>(null);

  // Cache compare trigger state
  const [isComparingCache, setIsComparingCache] = useState(false);
  const [cacheCompareResult, setCacheCompareResult] = useState<{
    success: boolean;
    message?: string;
    run_id?: string;
    correlation_id?: string;
  } | null>(null);
  const [cacheCompareError, setCacheCompareError] = useState<string | null>(null);

  const getAuthToken = () => {
    const authData = localStorage.getItem("tradecraft-auth");
    if (authData) {
      return JSON.parse(authData).state?.token;
    }
    return null;
  };

  const fetchCachedTickers = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/list`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data: CacheListResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          (data as unknown as { error?: string }).error ||
            "Failed to fetch cached tickers",
        );
      }

      setCachedTickers(data.tickers || []);
    } catch (err) {
      setListError(
        err instanceof Error ? err.message : "Failed to fetch cached tickers",
      );
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  // Load cronjob status on mount (cached tickers are now on-demand)
  useEffect(() => {
    // Fetch cronjob status (for Cached Tickers section)
    const fetchCronjobStatus = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE}/api/cache/cronjob-status`, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCronjobStatus(data);
        }
      } catch {
        // Silently fail - not critical
      }
    };
    
    // Fetch realtime refresh status (for Today's Prices section)
    const fetchRealtimeRefreshStatus = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE}/api/cache/realtime-refresh-status`, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (response.ok) {
          const data = await response.json();
          setRealtimeRefreshStatus(data);
        }
      } catch {
        // Silently fail - not critical
      }
    };
    
    fetchCronjobStatus();
    fetchRealtimeRefreshStatus();
  }, []);

  // Fetch today's prices from yfinance (on-demand)
  const fetchTodayPrices = useCallback(async () => {
    setIsLoadingTodayPrices(true);
    setTodayPricesError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/today-prices`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch today's prices");
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setTodayPrices(data.prices || []);
    } catch (err) {
      setTodayPricesError(
        err instanceof Error ? err.message : "Failed to fetch today's prices",
      );
    } finally {
      setIsLoadingTodayPrices(false);
    }
  }, []);

  // Trigger realtime refresh manually
  const handleTriggerRealtimeRefresh = async () => {
    setIsRefreshingRealtime(true);
    setRealtimeRefreshError(null);
    setRealtimeRefreshResult(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/trigger-realtime-refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to trigger realtime refresh");
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setRealtimeRefreshResult(data);
      
      // Refresh cronjob status after trigger
      const statusResponse = await fetch(`${API_BASE}/api/cache/cronjob-status`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setCronjobStatus(statusData);
      }
    } catch (err) {
      setRealtimeRefreshError(
        err instanceof Error ? err.message : "Failed to trigger realtime refresh",
      );
    } finally {
      setIsRefreshingRealtime(false);
    }
  };

  // Trigger cache comparison (RAM vs Redis)
  const handleTriggerCacheCompare = async () => {
    setIsComparingCache(true);
    setCacheCompareError(null);
    setCacheCompareResult(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/trigger-cache-compare`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to trigger cache comparison");
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setCacheCompareResult(data);
    } catch (err) {
      setCacheCompareError(
        err instanceof Error ? err.message : "Failed to trigger cache comparison",
      );
    } finally {
      setIsComparingCache(false);
    }
  };

  const handleWarmAllCache = async () => {
    if (!password) {
      setWarmAllError("Password is required");
      return;
    }

    setIsWarmingAll(true);
    setWarmAllError(null);
    setWarmAllResult(null);
    setShowPasswordModal(false);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/warm-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          password: password,
          start_date: "2000-01-01", // Fetch all available historical data from Y2K
          clear_old_data: clearOldData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to warm all cache");
      }

      setWarmAllResult({
        processed: data.tickersProcessed || 0,
        failed: data.tickersFailed || 0,
        skipped: data.tickersSkipped || 0,
        gapsFilled: data.gapsFilled || 0,
        nansFixed: data.nansFixed || 0,
        failedTickers: data.failedTickers || [],
      });

      // Refresh the cached tickers list
      fetchCachedTickers();
    } catch (err) {
      setWarmAllError(
        err instanceof Error ? err.message : "Failed to warm all cache",
      );
    } finally {
      setIsWarmingAll(false);
      setPassword("");
      setClearOldData(false);
    }
  };

  const handleWarmRecentCache = async () => {
    if (!recentPassword) {
      setWarmRecentError("Password is required");
      return;
    }

    setIsWarmingRecent(true);
    setWarmRecentError(null);
    setWarmRecentResult(null);
    setShowRecentPasswordModal(false);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/warm-recent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          password: recentPassword,
          days: 7,
          clear_old_data: recentClearOldData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to warm recent cache");
      }

      setWarmRecentResult({
        processed: data.tickersProcessed || 0,
        failed: data.tickersFailed || 0,
        skipped: data.tickersSkipped || 0,
        gapsFilled: data.gapsFilled || 0,
        nansFixed: data.nansFixed || 0,
        failedTickers: data.failedTickers || [],
      });

      // Refresh the cached tickers list
      fetchCachedTickers();
    } catch (err) {
      setWarmRecentError(
        err instanceof Error ? err.message : "Failed to warm recent cache",
      );
    } finally {
      setIsWarmingRecent(false);
      setRecentPassword("");
      setRecentClearOldData(false);
    }
  };

  const handleWarmCache = async () => {
    const tickerList = tickers
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter((t) => t.length > 0);

    if (tickerList.length === 0) {
      setWarmError("Please enter at least one ticker symbol");
      return;
    }

    setIsWarming(true);
    setWarmError(null);
    setWarmResults(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/cache/warm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          tickers: tickerList,
          start_date: startDate,
        }),
      });

      const data: WarmResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          (data as unknown as { error?: string }).error ||
            "Failed to warm cache",
        );
      }

      // Convert API response format to WarmResult array
      const resultsArray: WarmResult[] = [];

      // Process successful tickers from results object
      if (data.results && typeof data.results === "object") {
        for (const [ticker, records] of Object.entries(data.results)) {
          resultsArray.push({
            ticker,
            status: "success",
            records: records as number,
          });
        }
      }

      // Process failed tickers
      if (data.failed && Array.isArray(data.failed)) {
        for (const ticker of data.failed) {
          resultsArray.push({
            ticker,
            status: "failed",
            error: "Failed to fetch data",
          });
        }
      }

      setWarmResults(resultsArray);
    } catch (err) {
      setWarmError(err instanceof Error ? err.message : "Failed to warm cache");
    } finally {
      setIsWarming(false);
    }
  };

  const handleInspectCache = async () => {
    const ticker = inspectTicker.trim().toUpperCase();
    if (!ticker) {
      setInspectError("Please enter a ticker symbol");
      return;
    }

    setIsInspecting(true);
    setInspectError(null);
    setInspectResult(null);

    try {
      const token = getAuthToken();
      // Use Go API which reads directly from Redis cache
      const response = await fetch(`${API_BASE}/api/cache/inspect/${ticker}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data: InspectResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          (data as unknown as { error?: string }).error ||
            "Failed to inspect cache",
        );
      }

      // Transform API response to display format
      const transformedResult: InspectResponse = {
        ...data,
        status: data.exists ? "cached" : "not_cached",
        records: data.total_rows,
        date_range:
          data.first_date && data.last_date
            ? `${data.first_date} to ${data.last_date}`
            : undefined,
      };

      setInspectResult(transformedResult);
    } catch (err) {
      setInspectError(
        err instanceof Error ? err.message : "Failed to inspect cache",
      );
    } finally {
      setIsInspecting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Manage cache and system configuration"
        backgroundImage="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8 max-w-4xl mx-auto space-y-6">
        {/* Admin Section - only visible to admins */}
        <AdminSection />

        {/* Warm Cache Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-surface-100">
                Warm Price Cache
              </h2>
              <p className="text-sm text-surface-400">
                Pre-load historical price data for faster backtests
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Ticker Symbols (comma-separated)
              </label>
              <input
                type="text"
                value={tickers}
                onChange={(e) => setTickers(e.target.value)}
                placeholder="AAPL, MSFT, GOOGL, SPY, QQQ..."
                className="input-field w-full"
              />
              <p className="text-xs text-surface-500 mt-1">
                Enter ticker symbols separated by commas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field w-full sm:w-48"
              />
              <p className="text-xs text-surface-500 mt-1">
                Historical data will be fetched from this date
              </p>
            </div>

            <button
              onClick={handleWarmCache}
              disabled={isWarming || !tickers.trim()}
              className="btn-primary px-6 py-2.5 flex items-center gap-2"
            >
              {isWarming ? (
                <>
                  <span className="spinner" />
                  Warming Cache...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  Warm Cache
                </>
              )}
            </button>

            {warmError && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-400 text-sm">{warmError}</span>
              </div>
            )}

            {warmResults && (
              <div className="bg-surface-800/50 rounded-xl p-4 border border-surface-700/50">
                <h3 className="text-sm font-medium text-surface-200 mb-3">
                  Results
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                  {warmResults.map((result, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        result.status === "success" ||
                        result.status === "cached"
                          ? "bg-accent-500/10 border border-accent-500/30"
                          : "bg-red-500/10 border border-red-500/30"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {result.status === "success" ||
                        result.status === "cached" ? (
                          <svg
                            className="w-4 h-4 text-accent-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4 text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        )}
                        <span className="font-mono text-sm text-surface-200">
                          {result.ticker}
                        </span>
                      </div>
                      <span className="text-xs text-surface-400">
                        {result.records
                          ? `${result.records} records`
                          : result.error || result.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Inspect Cache Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-accent-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-accent-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-surface-100">
                Inspect Cache
              </h2>
              <p className="text-sm text-surface-400">
                Check if a ticker is cached and view data range
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Ticker Symbol
              </label>
              <input
                type="text"
                value={inspectTicker}
                onChange={(e) => setInspectTicker(e.target.value.toUpperCase())}
                placeholder="SPY"
                className="input-field w-full"
                onKeyDown={(e) => e.key === "Enter" && handleInspectCache()}
              />
            </div>
            <button
              onClick={handleInspectCache}
              disabled={isInspecting || !inspectTicker.trim()}
              className="btn-secondary px-6 py-2.5 flex items-center gap-2"
            >
              {isInspecting ? (
                <>
                  <span className="spinner" />
                  Checking...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  Inspect
                </>
              )}
            </button>
          </div>

          {inspectError && (
            <div className="mt-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{inspectError}</span>
            </div>
          )}

          {inspectResult && (
            <div className="mt-4 bg-surface-800/50 rounded-xl p-4 border border-surface-700/50">
              <div className="flex items-center gap-3 mb-3">
                <span className="font-mono text-lg text-surface-100">
                  {inspectResult.ticker}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    inspectResult.status === "cached"
                      ? "bg-accent-500/20 text-accent-400"
                      : "bg-yellow-500/20 text-yellow-400"
                  }`}
                >
                  {inspectResult.status === "cached" ? "Cached" : "Not Cached"}
                </span>
              </div>
              {inspectResult.records && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-surface-500">Records:</span>
                    <span className="ml-2 text-surface-200">
                      {inspectResult.records.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-surface-500">Date Range:</span>
                    <span className="ml-2 text-surface-200">
                      {inspectResult.date_range}
                    </span>
                  </div>
                </div>
              )}
              {inspectResult.recent_data && inspectResult.recent_data.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-surface-300 mb-2">Last 7 Days</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-surface-700">
                          <th className="text-left py-2 px-2 text-surface-400 font-medium">Date</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">Open</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">High</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">Low</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">Close</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">Adj Close</th>
                          <th className="text-right py-2 px-2 text-surface-400 font-medium">Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inspectResult.recent_data.map((row, idx) => (
                          <tr key={idx} className="border-b border-surface-700/50 hover:bg-surface-700/30">
                            <td className="py-2 px-2 text-surface-200 font-mono">{row.date}</td>
                            <td className="py-2 px-2 text-right text-surface-300">
                              {row.open !== null && row.open !== undefined ? row.open.toFixed(2) : <span className="text-red-400">NaN</span>}
                            </td>
                            <td className="py-2 px-2 text-right text-surface-300">
                              {row.high !== null && row.high !== undefined ? row.high.toFixed(2) : <span className="text-red-400">NaN</span>}
                            </td>
                            <td className="py-2 px-2 text-right text-surface-300">
                              {row.low !== null && row.low !== undefined ? row.low.toFixed(2) : <span className="text-red-400">NaN</span>}
                            </td>
                            <td className="py-2 px-2 text-right text-surface-200 font-medium">
                              {row.close !== null && row.close !== undefined ? row.close.toFixed(2) : <span className="text-red-400">NaN</span>}
                            </td>
                            <td className="py-2 px-2 text-right text-surface-300">
                              {row.adj_close !== null && row.adj_close !== undefined ? row.adj_close.toFixed(2) : <span className="text-red-400">NaN</span>}
                            </td>
                            <td className="py-2 px-2 text-right text-surface-400">
                              {row.volume !== null && row.volume !== undefined ? row.volume.toLocaleString() : <span className="text-red-400">NaN</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cached Tickers List */}
        <div className="glass-card p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-surface-100">
                  Cached Tickers
                </h2>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-surface-400">
                  <span>{cachedTickers.length} tickers in cache</span>
                  {cronjobStatus?.has_run && (
                    <>
                      <span className="text-surface-600 hidden sm:inline">•</span>
                      <span className="flex items-center gap-1.5 w-full sm:w-auto">
                        <svg className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Last cronjob: <span className="text-surface-300">{formatTimestampTo12Hour(cronjobStatus.timestamp_est || '')}</span></span>
                        {cronjobStatus.tickers_processed && (
                          <span className="text-surface-500 hidden md:inline">({cronjobStatus.tickers_processed} tickers, {cronjobStatus.duration_seconds}s)</span>
                        )}
                      </span>
                    </>
                  )}
                  {cronjobStatus && !cronjobStatus.has_run && (
                    <>
                      <span className="text-surface-600 hidden sm:inline">•</span>
                      <span className="text-yellow-400/70 text-xs">No cronjob run yet</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowPasswordModal(true)}
                disabled={isWarmingAll}
                className="btn-primary px-4 py-2 flex items-center gap-2"
              >
                {isWarmingAll ? (
                  <>
                    <span className="spinner" />
                    Warming All...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
                      />
                    </svg>
                    Warm All
                  </>
                )}
              </button>
              <button
                onClick={() => setShowRecentPasswordModal(true)}
                disabled={isWarmingRecent}
                className="btn-secondary px-4 py-2 flex items-center gap-2"
              >
                {isWarmingRecent ? (
                  <>
                    <span className="spinner" />
                    Warming 7 Days...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Warm 7 Days
                  </>
                )}
              </button>
              <button
                onClick={fetchCachedTickers}
                disabled={isLoadingList}
                className="btn-secondary px-4 py-2 flex items-center gap-2"
              >
                {isLoadingList ? (
                  <span className="spinner" />
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                {cachedTickers.length === 0 ? "Load Tickers" : "Refresh"}
              </button>
              <button
                onClick={handleTriggerRealtimeRefresh}
                disabled={isRefreshingRealtime}
                className="btn-secondary px-4 py-2 flex items-center gap-2 border-orange-500/50 hover:border-orange-400"
              >
                {isRefreshingRealtime ? (
                  <>
                    <span className="spinner" />
                    Updating Prices...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 text-orange-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span className="text-orange-400">Trigger Realtime Refresh</span>
                  </>
                )}
              </button>
              <button
                onClick={handleTriggerCacheCompare}
                disabled={isComparingCache}
                className="btn-secondary px-4 py-2 flex items-center gap-2 border-cyan-500/50 hover:border-cyan-400"
              >
                {isComparingCache ? (
                  <>
                    <span className="spinner" />
                    Comparing...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                    <span className="text-cyan-400">Compare RAM vs Redis</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Warm All Result/Error */}
          {warmAllError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{warmAllError}</span>
            </div>
          )}

          {warmAllResult && (
            <div className="mb-4 bg-accent-500/10 border border-accent-500/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  className="w-5 h-5 text-accent-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-accent-400 font-medium">
                  Full Warm Complete
                </span>
              </div>
              <div className="text-sm text-surface-300 space-y-1 ml-7">
                <div>Processed: {warmAllResult.processed} tickers</div>
                {warmAllResult.skipped !== undefined && warmAllResult.skipped > 0 && (
                  <div>Skipped (up-to-date): {warmAllResult.skipped}</div>
                )}
                {warmAllResult.gapsFilled !== undefined && warmAllResult.gapsFilled > 0 && (
                  <div>Gaps filled: {warmAllResult.gapsFilled}</div>
                )}
                {warmAllResult.nansFixed !== undefined && warmAllResult.nansFixed > 0 && (
                  <div>NaN values fixed: {warmAllResult.nansFixed}</div>
                )}
                {warmAllResult.failed > 0 && (
                  <div className="text-red-400">Failed: {warmAllResult.failed}</div>
                )}
                {warmAllResult.failedTickers && warmAllResult.failedTickers.length > 0 && (
                  <div className="mt-2 text-red-400">
                    <div className="font-medium">Failed Tickers:</div>
                    <ul className="list-disc list-inside ml-2">
                      {warmAllResult.failedTickers.map((f, i) => (
                        <li key={i} className="text-xs">
                          <span className="font-mono">{f.ticker}</span>: {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Warm Recent Result/Error */}
          {warmRecentError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{warmRecentError}</span>
            </div>
          )}

          {warmRecentResult && (
            <div className="mb-4 bg-blue-500/10 border border-blue-500/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-blue-400 font-medium">
                  7-Day Warm Complete
                </span>
              </div>
              <div className="text-sm text-surface-300 space-y-1 ml-7">
                <div>Processed: {warmRecentResult.processed} tickers</div>
                {warmRecentResult.skipped !== undefined && warmRecentResult.skipped > 0 && (
                  <div>Skipped (up-to-date): {warmRecentResult.skipped}</div>
                )}
                {warmRecentResult.gapsFilled !== undefined && warmRecentResult.gapsFilled > 0 && (
                  <div>Gaps filled: {warmRecentResult.gapsFilled}</div>
                )}
                {warmRecentResult.nansFixed !== undefined && warmRecentResult.nansFixed > 0 && (
                  <div>NaN values fixed: {warmRecentResult.nansFixed}</div>
                )}
                {warmRecentResult.failed > 0 && (
                  <div className="text-red-400">Failed: {warmRecentResult.failed}</div>
                )}
                {warmRecentResult.failedTickers && warmRecentResult.failedTickers.length > 0 && (
                  <div className="mt-2 text-red-400">
                    <div className="font-medium">Failed Tickers:</div>
                    <ul className="list-disc list-inside ml-2">
                      {warmRecentResult.failedTickers.map((f, i) => (
                        <li key={i} className="text-xs">
                          <span className="font-mono">{f.ticker}</span>: {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Realtime Refresh Result/Error */}
          {realtimeRefreshError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{realtimeRefreshError}</span>
            </div>
          )}

          {realtimeRefreshResult && (
            <div className="mb-4 bg-orange-500/10 border border-orange-500/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  className="w-5 h-5 text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span className="text-orange-400 font-medium">
                  {realtimeRefreshResult.status === 'started' ? 'Realtime Price Refresh Started' : 'Realtime Price Refresh Complete'}
                </span>
              </div>
              <div className="text-sm text-surface-300 space-y-1 ml-7">
                {realtimeRefreshResult.message && (
                  <div>{realtimeRefreshResult.message}</div>
                )}
                {realtimeRefreshResult.tickers_updated !== undefined && (
                  <div>Updated: {realtimeRefreshResult.tickers_updated} / {realtimeRefreshResult.tickers_total} tickers</div>
                )}
                {realtimeRefreshResult.duration_seconds !== undefined && (
                  <div>Duration: {realtimeRefreshResult.duration_seconds}s</div>
                )}
                {realtimeRefreshResult.timestamp_est && (
                  <div>Timestamp: {realtimeRefreshResult.timestamp_est}</div>
                )}
              </div>
            </div>
          )}

          {/* Cache Compare Result/Error */}
          {cacheCompareError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{cacheCompareError}</span>
            </div>
          )}

          {cacheCompareResult && (
            <div className="mb-4 bg-cyan-500/10 border border-cyan-500/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                <span className="text-cyan-400 font-medium">
                  Cache Comparison Triggered
                </span>
              </div>
              <div className="text-sm text-surface-300 space-y-1 ml-7">
                {cacheCompareResult.message && (
                  <div>{cacheCompareResult.message}</div>
                )}
                <div className="text-xs text-surface-500 mt-1">Check Run History for full results</div>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                placeholder="Search tickers..."
                className="input-field w-full pl-10"
              />
            </div>
          </div>

          {listError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{listError}</span>
            </div>
          )}

          {isLoadingList ? (
            <div className="flex items-center justify-center py-8">
              <span className="spinner mr-2" />
              <span className="text-surface-400">
                Loading cached tickers...
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-700/50">
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200"
                      onClick={() => {
                        if (sortField === "ticker") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc",
                          );
                        } else {
                          setSortField("ticker");
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Ticker
                        {sortField === "ticker" && (
                          <svg
                            className={`w-3 h-3 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200"
                      onClick={() => {
                        if (sortField === "records") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc",
                          );
                        } else {
                          setSortField("records");
                          setSortDirection("desc");
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Records
                        {sortField === "records" && (
                          <svg
                            className={`w-3 h-3 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200"
                      onClick={() => {
                        if (sortField === "start_date") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc",
                          );
                        } else {
                          setSortField("start_date");
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Start Date
                        {sortField === "start_date" && (
                          <svg
                            className={`w-3 h-3 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200"
                      onClick={() => {
                        if (sortField === "end_date") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc",
                          );
                        } else {
                          setSortField("end_date");
                          setSortDirection("desc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        End Date
                        {sortField === "end_date" && (
                          <svg
                            className={`w-3 h-3 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200"
                      onClick={() => {
                        if (sortField === "size_bytes") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc",
                          );
                        } else {
                          setSortField("size_bytes");
                          setSortDirection("desc");
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Size
                        {sortField === "size_bytes" && (
                          <svg
                            className={`w-3 h-3 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-700/30">
                  {cachedTickers
                    .filter((t) => t.ticker.includes(searchQuery))
                    .sort((a, b) => {
                      let comparison = 0;
                      switch (sortField) {
                        case "ticker":
                          comparison = a.ticker.localeCompare(b.ticker);
                          break;
                        case "records":
                          comparison = (a.records || 0) - (b.records || 0);
                          break;
                        case "start_date":
                          comparison = (a.start_date || "").localeCompare(
                            b.start_date || "",
                          );
                          break;
                        case "end_date":
                          comparison = (a.end_date || "").localeCompare(
                            b.end_date || "",
                          );
                          break;
                        case "size_bytes":
                          comparison =
                            (a.size_bytes || 0) - (b.size_bytes || 0);
                          break;
                      }
                      return sortDirection === "asc" ? comparison : -comparison;
                    })
                    .map((ticker) => (
                      <tr
                        key={ticker.ticker}
                        className="hover:bg-surface-800/30"
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-surface-100">
                            {ticker.ticker}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-surface-300">
                            {ticker.records?.toLocaleString() || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-surface-400">
                            {ticker.start_date || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-surface-400">
                            {ticker.end_date || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-surface-500">
                            {ticker.size_bytes
                              ? `${(ticker.size_bytes / 1024).toFixed(1)} KB`
                              : "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {cachedTickers.filter((t) => t.ticker.includes(searchQuery))
                .length === 0 && (
                <div className="text-center py-8 text-surface-500">
                  {searchQuery
                    ? "No tickers match your search"
                    : "No cached tickers found"}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Today's Prices Section */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-surface-100">
                  Today's Prices (Live)
                </h2>
                <div className="flex items-center gap-3 text-sm text-surface-400">
                  <span>Current prices from yFinance</span>
                  {realtimeRefreshStatus?.has_run && (
                    <>
                      <span className="text-surface-600">•</span>
                      <span className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Last cron: <span className="text-surface-300">{formatTimestampTo12Hour(realtimeRefreshStatus.timestamp_est || '')}</span></span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={fetchTodayPrices}
              disabled={isLoadingTodayPrices}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              {isLoadingTodayPrices ? (
                <>
                  <span className="spinner" />
                  Loading Prices...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                  {todayPrices.length === 0 ? "Fetch Today's Prices" : "Refresh Prices"}
                </>
              )}
            </button>
          </div>

          {todayPricesError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{todayPricesError}</span>
            </div>
          )}

          {todayPrices.length === 0 && !isLoadingTodayPrices && !todayPricesError ? (
            <div className="text-center py-8 text-surface-500">
              Click "Fetch Today's Prices" to load current market prices
            </div>
          ) : todayPrices.length > 0 && (
            <>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={todayPricesSearchQuery}
                    onChange={(e) => setTodayPricesSearchQuery(e.target.value.toUpperCase())}
                    placeholder="Search tickers..."
                    className="input-field w-full pl-10"
                  />
                </div>
              </div>

              <div className="text-sm text-surface-400 mb-2">
                Showing {todayPrices.filter(p => p.ticker.includes(todayPricesSearchQuery)).length} of {todayPrices.length} tickers
              </div>

              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="sticky top-0 bg-surface-900">
                    <tr className="border-b border-surface-700/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                        Ticker
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                        Open
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                        High
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                        Low
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                        Close
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                        Volume
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-700/30">
                    {todayPrices
                      .filter((p) => p.ticker.includes(todayPricesSearchQuery))
                      .map((price) => (
                        <tr
                          key={price.ticker}
                          className="hover:bg-surface-800/30"
                        >
                          <td className="px-4 py-2">
                            <span className="font-mono text-sm text-surface-100">
                              {price.ticker}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-sm text-surface-400">
                              {price.open !== null ? `$${price.open.toFixed(2)}` : "-"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-sm text-green-400">
                              {price.high !== null ? `$${price.high.toFixed(2)}` : "-"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-sm text-red-400">
                              {price.low !== null ? `$${price.low.toFixed(2)}` : "-"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-sm text-surface-100 font-medium">
                              ${price.close.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="text-sm text-surface-500">
                              {price.volume !== null ? price.volume.toLocaleString() : "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Password Modal for Warm All */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-surface-100">
                  Warm All Cache
                </h3>
                <p className="text-sm text-surface-400">
                  Smart warm for 1200+ tickers from year 2000
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="input-field w-full"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleWarmAllCache()}
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearOldData}
                  onChange={(e) => setClearOldData(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                />
                <span className="text-sm text-surface-300">
                  Clear old data first
                </span>
              </label>
              <p className="text-xs text-surface-500 mt-1 ml-6">
                If checked, deletes ALL cached data and fetches fresh. Otherwise, only fills gaps and fixes NaN values.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword("");
                  setClearOldData(false);
                }}
                className="btn-secondary flex-1 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleWarmAllCache}
                disabled={!password}
                className="btn-primary flex-1 py-2"
              >
                Start Warming
              </button>
            </div>

            <p className="mt-4 text-xs text-surface-500 text-center">
              ⚠️ This may take 10-30 minutes to complete
            </p>
          </div>
        </div>
      )}

      {/* Password Modal for Warm Recent (7 Days) */}
      {showRecentPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-surface-100">
                  Warm Last 7 Days
                </h3>
                <p className="text-sm text-surface-400">
                  Update recent data for all cached tickers
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={recentPassword}
                onChange={(e) => setRecentPassword(e.target.value)}
                placeholder="Enter password"
                className="input-field w-full"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleWarmRecentCache()}
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recentClearOldData}
                  onChange={(e) => setRecentClearOldData(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                />
                <span className="text-sm text-surface-300">
                  Clear recent data first
                </span>
              </label>
              <p className="text-xs text-surface-500 mt-1 ml-6">
                If checked, clears last 7 days and re-fetches. Otherwise, only fills gaps and fixes NaN values.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRecentPasswordModal(false);
                  setRecentPassword("");
                  setRecentClearOldData(false);
                }}
                className="btn-secondary flex-1 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleWarmRecentCache}
                disabled={!recentPassword}
                className="btn-primary flex-1 py-2"
              >
                Start Warming
              </button>
            </div>

            <p className="mt-4 text-xs text-surface-500 text-center">
              ⚠️ This should take 2-5 minutes to complete
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
